import { WarningCircle } from '@phosphor-icons/react';
import React, { useContext, useEffect, useState } from 'react';

import Buttons from '@/components/Buttons';
import CaretRight from '@/components/Icons/CaretRight';
import CheckCircle from '@/components/Icons/CheckCircle';
import Signature from '@/components/Icons/Signature';
import TaskEfficiencyIndicator from '@/components/TaskEfficiencyIndicator';
import { useUser } from '@/hooks';
import useNoodleApi from '@/hooks/useNoodleApi';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import ESignatureUploadModal from '@/modals/ESignatureModal';
import SkipTaskConfirmationModal from '@/modals/SkipTaskConfirmationModal';
import { useOpenESignatureRequestPane } from '@/panes/ESignatureRequestHooks';
import * as tsClient from '@/tsClient';
import { type Creator, ESignatureRequestStatus, MESSAGE_ATTACHMENT_REFERENCE_TYPES } from '@/typings/api-models';

import s from './ESignatureRequestCard.module.scss';

type Props = {
  creator: Pick<Creator, 'slug'>;
  request: {
    eSignatureRequestId: string;
    uploadedDocumentId: string | null;
    signedDocumentId: string | null;
    anvilCertificateForSignedDocumentId: string | null;
    status: ESignatureRequestStatus;
    fileName: string | null;
    userWorkflowId: string;
    creatorId: string;
  };
  canClick: boolean;
  canSkip: boolean;
  setIsFulfilled?: (b: boolean) => void;
  setIsSkipped?: (b: boolean) => void;
  onChange?: () => Promise<void>;
  integrationSlugs?: string[];
  userWorkflowId?: string;
  assigneeIds?: string[];
  creatorSlug?: string;
  reload?: () => Promise<void>;
  status: ESignatureRequestStatus;
};

const ESignatureRequestCard: React.FC<Props> = ({
  setIsFulfilled,
  setIsSkipped,
  canClick,
  canSkip,
  request,
  reload,
  status,
}) => {
  const isSkipped = status === ESignatureRequestStatus.SKIPPED;
  const isCompleted = status === ESignatureRequestStatus.COMPLETED;
  const isVoided = status === ESignatureRequestStatus.VOIDED;
  const [skipTaskModalOpen, setSkipTaskModalOpen] = useState(false);
  const { isInDashboard } = useContext(DashboardContext);
  const [isESignatureRequestModalOpen, setIsESignatureRequestModalOpen] = useState(false);

  const openESignatureRequestPane = useOpenESignatureRequestPane();
  const [user] = useUser();
  const isCreator = user?.isCreator;

  const { getData: updateESignatureRequest } = useNoodleApi(tsClient.eSignatureRequests.updateESignatureRequest);

  useEffect(() => {
    setIsSkipped?.(isSkipped);
  }, [isSkipped, setIsSkipped]);

  useEffect(() => {
    setIsFulfilled?.(isCompleted);
  }, [isCompleted, setIsFulfilled]);

  const handleClick = (): void => {
    if (isSkipped) {
      return;
    }
    if (request.uploadedDocumentId || !isCreator) {
      openESignatureRequestPane({ eSignatureRequestId: request.eSignatureRequestId });
    } else {
      setIsESignatureRequestModalOpen(true);
    }
  };

  const handleSkipTask = async (): Promise<void> => {
    await updateESignatureRequest({
      eSignatureRequestId: request.eSignatureRequestId,
      skippedAt: new Date().toISOString(),
    });
    setSkipTaskModalOpen(false);
    if (reload) reload();
  };

  const handleUnskipTask = async (): Promise<void> => {
    await updateESignatureRequest({
      eSignatureRequestId: request.eSignatureRequestId,
      skippedAt: null,
    });
    if (reload) reload();
  };

  return (
    <>
      {skipTaskModalOpen && (
        <SkipTaskConfirmationModal
          onClose={() => setSkipTaskModalOpen(false)}
          onConfirm={handleSkipTask}
          taskType={MESSAGE_ATTACHMENT_REFERENCE_TYPES.E_SIGNATURE_REQUEST}
        />
      )}
      <Buttons
        key={request.eSignatureRequestId}
        className={s.container}
        isWrapper
        isFullWidth
        style={isSkipped ? { backgroundColor: 'var(--color-gray-25)', borderRadius: 8, cursor: 'default' } : {}}
        onClick={handleClick}
      >
        {isCompleted ? (
          <CheckCircle size={16} weight='fill' color='var(--color-success)' />
        ) : isVoided ? (
          <WarningCircle size={16} weight='fill' color='var(--color-warning)' />
        ) : (
          <figure>
            {isSkipped && <Signature size={20} color='var(--color-gray-100)' />}
            {!isSkipped && !isVoided && <Signature size={20} color='var(--color-primary)' />}
          </figure>
        )}
        <div>
          <div className={s.type}>
            <p
              className={isCompleted ? 'caption-bold' : 'body-sm-bold'}
              style={isCompleted ? { color: 'var(--color-gray-75)' } : { lineHeight: 1.4 }}
            >
              eSignature Request {isVoided && <span>for {request.fileName}</span>}
            </p>
          </div>
          {isSkipped && <p className='caption'>This task is no longer required.</p>}
          {isVoided && (
            <p className='caption' style={{ color: 'var(--color-error)' }}>
              Document voided
            </p>
          )}
          {!isSkipped && !isVoided && <p className='caption'>{request.fileName}</p>}
        </div>
        {isSkipped && !isVoided && isInDashboard && (
          <Buttons
            onClick={() => {
              handleUnskipTask();
            }}
          >
            <div className={s.open}>Unskip</div>
          </Buttons>
        )}
        {canClick && !isSkipped && (
          <>
            {isCompleted || isVoided ? (
              <CaretRight size={12} color='var(--color-gray-100)' />
            ) : (
              <div
                className={s.open}
                {...(!isCompleted &&
                  !isInDashboard && {
                    style: {
                      backgroundColor: 'var(--color-primary)',
                      border: '1px solid var(--color-primary)',
                      color: 'var(--color-gray-0)',
                    },
                  })}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                }}
              >
                View
              </div>
            )}
          </>
        )}
      </Buttons>
      <TaskEfficiencyIndicator
        className='px-3 py-1'
        referenceType='e_signature_request'
        referenceId={request.eSignatureRequestId}
        isSkipped={isSkipped}
      />
      {canSkip && !isSkipped && !isVoided && !isCompleted && (
        <Buttons
          onClick={(e) => {
            e.stopPropagation();
            setSkipTaskModalOpen(true);
          }}
          className={s.skip}
        >
          Skip
        </Buttons>
      )}

      {isESignatureRequestModalOpen && (
        <ESignatureUploadModal
          isOpen
          onClose={() => setIsESignatureRequestModalOpen(false)}
          onContinue={openESignatureRequestPane}
          eSignatureRequestId={request.eSignatureRequestId}
          eSignatureRequestStatus={request.status}
          reloadWorkflow={reload}
        />
      )}
    </>
  );
};

export default ESignatureRequestCard;
